import React, { useEffect, useState } from "react";
import api, { formatPHP, formatDateTime, formatErrorDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { hasAnyRole } from "@/lib/perms";
import { PageHeader, Surface, TableWrap, Empty } from "@/components/app/Primitives";
import EmployeePicker from "@/components/app/EmployeePicker";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Plus, Gift, Check, X, BadgeDollarSign } from "lucide-react";

const STATUS_BADGE = {
    pending_approval: "badge badge-pending", approved: "badge badge-info",
    credited: "badge badge-approved", rejected: "badge badge-destructive",
};

export default function Bonuses() {
    const { user, activeCompanyId } = useAuth();
    const isMaker = hasAnyRole(user, activeCompanyId, ["hr_manager"]);
    const isChecker = hasAnyRole(user, activeCompanyId, ["accounting_manager"]);
    const [data, setData] = useState(null);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ employee_id: "", kind: "bonus", amount: "", description: "" });

    const load = () => api.get("/bonuses").then((r) => setData(r.data)).catch(() => {});
    useEffect(() => { load(); }, []);

    const propose = async () => {
        if (!form.employee_id || !form.amount) return toast.error("Pick the employee and the amount");
        try {
            await api.post("/bonuses", { ...form, amount: Number(form.amount) });
            toast.success("Scheduled — waiting for the Accounting Manager's approval");
            setOpen(false); setForm({ employee_id: "", kind: "bonus", amount: "", description: "" });
            load();
        } catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail)); }
    };

    const decide = async (b, decision) => {
        let reason = "";
        if (decision === "rejected") { reason = window.prompt("Reason for rejection:") || ""; if (!reason) return; }
        try {
            await api.post(`/bonuses/${b.id}/decide`, { decision, reason });
            toast.success(decision === "approved"
                ? `Approved — ₱${Number(b.amount).toLocaleString()} credits on the next payroll cutoff`
                : "Rejected");
            load();
        } catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail)); }
    };

    return (
        <div>
            <PageHeader title="Bonuses & Commissions" subtitle="One-time bonuses and commission payouts. HR Manager schedules → Accounting Manager approves → credited automatically on the next payroll cutoff."
                actions={isMaker && (
                    <button className="h-9 px-3 rounded-md bg-primary text-white text-sm font-medium flex items-center gap-2" onClick={() => setOpen(true)} data-testid="new-bonus-btn">
                        <Plus className="size-4" /> Schedule bonus / commission
                    </button>
                )} />

            {data && (
                <div className="flex items-center gap-2 mb-3 text-xs">
                    <BadgeDollarSign className="size-4 text-primary" />
                    <span className="badge badge-pending">{data.pending} awaiting accounting approval</span>
                    <span className="text-muted-foreground">Maker-checker: HR Manager proposes, only the Accounting Manager can approve — no bypass.</span>
                </div>
            )}

            {data && !data.items.length && <Surface className="p-6"><Empty title="No bonuses scheduled" /></Surface>}
            {data?.items.length > 0 && (
                <TableWrap>
                    <thead><tr><th>Employee</th><th>Type</th><th className="num">Amount</th><th>Reason</th><th>Status</th><th>Credit</th><th>Actions</th></tr></thead>
                    <tbody>
                        {data.items.map((b) => (
                            <tr key={b.id} data-testid={`bonus-${b.id}`}>
                                <td><b>{b.employee_name}</b> <span className="text-xs text-muted-foreground">{b.employee_no}</span></td>
                                <td><span className={`badge ${b.kind === "commission" ? "badge-info" : "badge-draft"}`}>{String(b.kind || "").replaceAll("_", " ")}</span></td>
                                <td className="num font-mono font-semibold">{formatPHP(b.amount)}</td>
                                <td className="text-xs max-w-[220px]">{b.description || "—"}</td>
                                <td>
                                    <span className={STATUS_BADGE[b.status] || "badge"}>{b.status.replace(/_/g, " ")}</span>
                                    {b.rejection_reason && <span className="block text-[10px] text-muted-foreground">“{b.rejection_reason}”</span>}
                                </td>
                                <td className="text-xs">
                                    {b.status === "credited" ? <span className="text-emerald-700 font-mono">{b.credited_period}</span>
                                        : b.status === "approved" ? <span className="text-muted-foreground">next cutoff</span> : "—"}
                                </td>
                                <td>
                                    {b.status === "pending_approval" && isChecker ? (
                                        <div className="flex gap-1">
                                            <button className="h-7 px-2 text-xs rounded-md bg-emerald-600 text-white flex items-center gap-1" onClick={() => decide(b, "approved")} data-testid={`approve-bonus-${b.id}`}><Check className="size-3" /> Approve</button>
                                            <button className="h-7 px-2 text-xs rounded-md bg-destructive text-white flex items-center gap-1" onClick={() => decide(b, "rejected")}><X className="size-3" /></button>
                                        </div>
                                    ) : b.status === "pending_approval" ? <span className="text-[10px] text-muted-foreground">awaiting accounting</span> : <span className="text-[10px] text-muted-foreground">—</span>}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </TableWrap>
            )}

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader><DialogTitle className="flex items-center gap-2"><Gift className="size-4 text-primary" /> Schedule a one-time payout</DialogTitle></DialogHeader>
                    <div className="space-y-2">
                        <div><Label>Employee</Label><EmployeePicker value={form.employee_id} onChange={(v) => setForm({ ...form, employee_id: v })} testId="bonus-employee" /></div>
                        <div>
                            <Label>Type</Label>
                            <select className="h-9 w-full rounded-md border border-border bg-background px-2 text-sm" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} data-testid="bonus-kind">
                                <option value="bonus">One-time bonus</option>
                                <option value="commission">Commission</option>
                                <option value="14th_month">14th month pay</option>
                                <option value="performance">Performance bonus</option>
                                <option value="christmas">Christmas bonus</option>
                                <option value="anniversary">Anniversary bonus</option>
                                <option value="referral">Referral bonus</option>
                                <option value="attendance_incentive">Attendance incentive</option>
                                <option value="incentive">Incentive</option>
                            </select>
                        </div>
                        <div><Label>Amount (₱)</Label><Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="bonus-amount" /></div>
                        <div><Label>Reason / computation basis</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Q2 sales commission — 2% of ₱1.2M closed" data-testid="bonus-desc" /></div>
                        <div className="text-xs text-muted-foreground">Once the Accounting Manager approves, it is credited automatically as an earning on the next payroll run that gets computed.</div>
                    </div>
                    <DialogFooter><button className="h-9 px-3 rounded-md bg-primary text-white text-sm" onClick={propose} data-testid="bonus-submit">Submit for approval</button></DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
